import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface DiscoveryLayoutProps {
    activeTab: 'for-you' | 'categories' | 'following';
    onTabChange: (tab: 'for-you' | 'categories' | 'following') => void;
    children: React.ReactNode;
}

const DiscoveryLayout: React.FC<DiscoveryLayoutProps> = ({ activeTab, onTabChange, children }) => {
    const { t } = useTranslation('marketplace');

    const tabs = [
        { id: 'for-you', label: t('discovery.tabs.forYou') },
        { id: 'following', label: t('discovery.tabs.following') },
        { id: 'categories', label: t('discovery.tabs.categories') }
    ];

    return (
        <div className="flex flex-col h-full bg-white dark:bg-gray-950">
            {/* Segmented Control Header */}
            <div className="sticky top-14 z-40 bg-white/95 dark:bg-gray-950/95 backdrop-blur-md border-b border-gray-100 dark:border-gray-800 px-4 pt-2">
                <div className="flex bg-gray-100 dark:bg-gray-900 p-1 rounded-2xl mb-2 border border-gray-200 dark:border-gray-800">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => onTabChange(tab.id as 'for-you' | 'categories' | 'following')}
                            className="relative flex-1 py-2.5 text-xs font-black uppercase tracking-widest transition-colors duration-200 focus:outline-none"
                        >
                            <span className={`relative z-10 transition-colors duration-300 ${activeTab === tab.id ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                                {tab.label}
                            </span>
                            {activeTab === tab.id && (
                                <motion.div
                                    layoutId="activeTabDiscovery"
                                    className="absolute inset-0 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
                        className="w-full"
                    >
                        {children}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};

export default DiscoveryLayout;
